/**
 * <rz-search>: wraps a search form. Adds
 *  - predictive results under the field (/search/suggest + section rendering), with arrow-key navigation;
 *  - rotating "typed" placeholder prompts;
 *  - the product type dropdown (only submitted when a type is chosen).
 */
if (!customElements.get('rz-search')) {
  customElements.define(
    'rz-search',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.panel = this.querySelector('[data-predictive-panel]');
        this.cache = new Map();
        if (!this.input) return;

        if (this.panel) {
          this.input.setAttribute('role', 'combobox');
          this.input.setAttribute('aria-autocomplete', 'list');
          this.input.setAttribute('aria-expanded', 'false');
          this.input.setAttribute('aria-controls', this.panel.id);
          this.input.addEventListener('input', () => {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.suggest(), 250);
          });
          // After the click that focused the field (e.g. the header search button) has reached the document,
          // or its outside-click handler closes the panel straight away.
          this.input.addEventListener('focus', () => setTimeout(() => this.suggest()));
          this.input.form?.addEventListener('submit', () => this.remember(this.input.value));
          this.input.addEventListener('keydown', (e) => this.onKeydown(e));
          document.addEventListener('click', (e) => !this.contains(e.target) && this.close());
          this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && this.close());
        }

        // Panel style: one tab per result type.
        this.panel?.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-predictive-tab]');
          if (!tab) return;
          this.panel.querySelectorAll('[data-predictive-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
          this.panel.querySelectorAll('[data-predictive-tabpanel]').forEach((p) => (p.hidden = p.id !== tab.getAttribute('aria-controls')));
        });

        const type = this.querySelector('[data-product-type]');
        if (type) type.addEventListener('change', () => (type.name = type.value ? 'filter.p.product_type' : ''));

        this.startPrompts();
        this.setupVoice();
      }

      // Voice search: shown only where the browser supports speech recognition (Chrome, Edge, Safari).
      setupVoice() {
        const button = this.querySelector('[data-voice]');
        const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!button || !Recognition) return;
        button.hidden = false;
        button.addEventListener('click', () => {
          if (this.recognition) return this.recognition.stop();
          const r = (this.recognition = new Recognition());
          r.lang = document.documentElement.lang || 'bg';
          r.interimResults = true;
          r.onresult = (e) => {
            const result = e.results[e.results.length - 1];
            this.input.value = result[0].transcript;
            if (result.isFinal) this.input.form.requestSubmit();
          };
          r.onend = () => {
            this.recognition = null;
            button.setAttribute('aria-pressed', 'false');
          };
          button.setAttribute('aria-pressed', 'true');
          r.start();
        });
      }

      // Empty field: the shopper's last searches and the store's popular searches (Theme settings > Search).
      recent() {
        try {
          return JSON.parse(localStorage.getItem('rz-recent-searches')) || [];
        } catch (e) {
          return [];
        }
      }

      remember(value) {
        const q = value.trim();
        if (!q) return;
        const list = [q, ...this.recent().filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, 5);
        try {
          localStorage.setItem('rz-recent-searches', JSON.stringify(list));
        } catch (e) {}
      }

      showStart() {
        const root = window.Shopify?.routes?.root || '/';
        const groups = [
          ['recent', this.dataset.labelRecent, this.recent()],
          ['popular', this.dataset.labelPopular, (this.dataset.popular || '').split(',').map((t) => t.trim()).filter(Boolean)],
        ].filter(([, , terms]) => terms.length);
        if (!groups.length) return this.close();
        const results = document.createElement('div');
        results.className = 'predictive predictive--start';
        results.dataset.predictiveResults = '';
        groups.forEach(([key, label, terms]) => {
          const heading = Object.assign(document.createElement('p'), { className: 'predictive__heading', id: `predictive-h-${key}`, textContent: label });
          const list = document.createElement('ul');
          list.className = 'predictive__queries';
          list.setAttribute('role', 'group');
          list.setAttribute('aria-labelledby', heading.id);
          terms.forEach((term, i) => {
            const li = document.createElement('li');
            li.setAttribute('role', 'presentation');
            const a = Object.assign(document.createElement('a'), {
              className: 'predictive__option',
              id: `predictive-${key}-${i}`,
              href: `${root}search?q=${encodeURIComponent(term)}`,
              textContent: term,
            });
            a.setAttribute('role', 'option');
            li.append(a);
            list.append(li);
          });
          results.append(heading, list);
        });
        this.panel.replaceChildren(results);
        this.open();
      }

      async suggest() {
        const q = this.input.value.trim();
        if (!q) return this.showStart();
        const params = new URLSearchParams({
          q,
          'resources[type]': 'product,collection,page,article,query',
          'resources[limit]': this.dataset.limit || '6',
          'resources[options][fields]': this.dataset.fields,
          section_id: 'predictive-search',
        });
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest?${params}`;
        if (!this.cache.has(url)) this.cache.set(url, fetch(url).then((r) => r.text()));
        const html = await this.cache.get(url);
        if (this.input.value.trim() !== q) return; // a newer query is on its way
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // The whole results element moves in (not just its children): its class carries the max height and the style.
        const results = doc.querySelector('[data-predictive-results]');
        if (results) this.panel.replaceChildren(results);
        this.open();
      }

      open() {
        this.style.setProperty('--field-h', `${this.querySelector('form').offsetHeight}px`);
        this.panel.hidden = false;
        this.classList.add('is-open');
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        if (!this.panel || this.panel.hidden) return;
        this.panel.hidden = true;
        this.classList.remove('is-open');
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          if (!this.panel.hidden) {
            event.stopPropagation();
            this.close();
          }
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key) || this.panel.hidden) return;
        const options = [...this.panel.querySelectorAll('.predictive__option')].filter((o) => !o.closest('[hidden]'));
        const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
        if (event.key === 'Enter') {
          if (current >= 0) {
            event.preventDefault();
            options[current].click();
          }
          return;
        }
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? Math.min(options.length - 1, current + 1) : Math.max(0, current - 1);
        options.forEach((o, i) => o.setAttribute('aria-selected', String(i === next)));
        options[next]?.scrollIntoView({ block: 'nearest' });
        this.input.setAttribute('aria-activedescendant', options[next]?.id || '');
      }

      /* Placeholder prompts: type, pause, erase, next. */
      startPrompts() {
        const prompts = (this.dataset.prompts || '').split('|').filter(Boolean);
        if (prompts.length < 2) return;
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (this.dataset.promptsMobile !== 'true' && !matchMedia('(min-width: 990px)').matches) return;
        let p = 0;
        let i = 0;
        let deleting = false;
        const tick = () => {
          if (document.activeElement === this.input || this.input.value) return setTimeout(tick, 1000);
          const text = prompts[p];
          i += deleting ? -1 : 1;
          this.input.placeholder = text.slice(0, i);
          let delay = deleting ? 30 : 70;
          if (!deleting && i >= text.length) {
            deleting = true;
            delay = 2000;
          } else if (deleting && i <= 0) {
            deleting = false;
            p = (p + 1) % prompts.length;
            delay = 400;
          }
          setTimeout(tick, delay);
        };
        setTimeout(tick, 1500);
      }
    }
  );
}
